function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import Mark from "mark.js";
import { renderTextLayer } from "pdfjs-dist/build/pdf";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import useAsyncCache from "astrid-hooks/src/useAsyncCache";
import { negativeColor, positiveColor, warningColor } from "../../../helpers/color";
import Loader from "./Loader";
import Rect from "./Rect";
import Select from "./Select";
import { useHighlights } from "./state/highlights";
import { usePdf } from "./state/pdf";
import { useRects } from "./state/rects";
import { useSelection } from "./state/selection";
import { useSize } from "./state/size";
const ratio = window.devicePixelRatio || 1;
const Root = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "Root",
  componentId: "sc-1f5nrpa-0"
})(["display:flex;align-items:center;justify-content:center;flex-direction:column;padding:5px;"]));
const Content = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "Content",
  componentId: "sc-1f5nrpa-1"
})(["position:relative;"]));
const Canvas = /*#__PURE__*/memo(styled.canvas.withConfig({
  displayName: "Canvas",
  componentId: "sc-1f5nrpa-2"
})(["display:block;"]));
const Text = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "Text",
  componentId: "sc-1f5nrpa-3"
})(["position:absolute;top:0;left:0;width:100%;height:100%;line-height:1;> span{color:transparent;position:absolute;white-space:pre;transform-origin:0% 0%;}.negative,.positive,.warning{background-color:transparent;color:transparent;opacity:0.7;}.warning{border-bottom:2px solid ", ";}.negative{border-bottom:2px solid ", ";}.positive{border-bottom:2px solid ", ";cursor:pointer;}"], warningColor, negativeColor, positiveColor));
const Layer = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "Layer",
  componentId: "sc-1f5nrpa-4"
})(["position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;"]));
const Page = /*#__PURE__*/memo(({
  index,
  style
}) => {
  const text = useRef();
  const highlight = useRef();
  const canvasRef = useRef();
  const pdf = usePdf();
  const size = useSize();
  const rects = useRects();
  const selection = useSelection();
  const highlights = useHighlights();
  const [rendered, setRendered] = useState(false);
  const [textRendered, setTextRendered] = useState(false);
  const page = useAsyncCache(pdf.fingerprint + "page" + index, useCallback(() => pdf && pdf.getPage(index + 1), [index, pdf]));
  const textContent = useAsyncCache(pdf.fingerprint + "text" + index, useCallback(() => page && page.getTextContent({
    normalizeWhitespace: true,
    disableCombineTextItems: false
  }), [page]));
  const originalHeight = useMemo(() => page && page.getViewport({
    scale: 1
  }).height, [page]);
  const scale = size / originalHeight;
  const viewport = useMemo(() => page && page.getViewport({
    scale
  }), [page, scale]);
  const renderViewport = useMemo(() => page && page.getViewport({
    scale: scale * ratio
  }), [page, scale]);
  useEffect(() => {
    if (textRendered && highlights.length) {
      const mark = new Mark(highlight.current);
      highlights.forEach(({
        text,
        color,
        onClick
      }) => mark.mark(text, {
        className: color,
        diacritics: false,
        accuracy: "exactly",
        ignoreJoiners: true,
        acrossElements: true,
        separateWordSearch: false,
        each: element => element.onclick = onClick
      }));
      return () => mark.unmark();
    }
  }, [highlights, textRendered]);
  useEffect(() => {
    if (canvasRef.current && page && viewport && renderViewport && textContent) {
      try {
        let mounted = true;
        const canvas = canvasRef.current;
        const textLayerA = text.current;
        const textLayerB = highlight.current;
        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        const render = page.render({
          viewport: renderViewport,
          renderInteractiveForms: false,
          get canvasContext() {
            return canvas.getContext("2d");
          }
        });
        render.promise.then(() => {
          if (mounted) {
            setRendered(true);
            renderTextLayer({
              viewport,
              textContent,
              container: textLayerA
            }).promise.then(() => setTextRendered(true));
            renderTextLayer({
              viewport,
              textContent,
              container: textLayerB
            });
          }
        }).catch(() => {});
        return () => {
          mounted = false;
          canvas.width = 0;
          canvas.height = 0;
          textLayerA.innerHTML = "";
          textLayerB.innerHTML = "";
          render.cancel();
          setTextRendered(false);
        };
      } catch (error) {
        console.error(error);
      }
    }
  }, [page, renderViewport, textContent, viewport]);
  return /*#__PURE__*/React.createElement(Root, {
    style: style
  }, page && /*#__PURE__*/React.createElement(Content, {
    style: {
      width: viewport.width,
      height: viewport.height,
      "--scale-factor": scale
    }
  }, /*#__PURE__*/React.createElement(Canvas, {
    ref: canvasRef
  }), /*#__PURE__*/React.createElement(Text, {
    ref: text,
    "data-page": page.pageNumber
  }), /*#__PURE__*/React.createElement(Text, {
    ref: highlight,
    "data-page": page.pageNumber,
    style: {
      pointerEvents: "none"
    }
  }), /*#__PURE__*/React.createElement(Layer, null, (rects[page.pageNumber] || []).map((r, i) => /*#__PURE__*/React.createElement(Rect, _extends({
    key: i
  }, r))), selection && selection.page === page.pageNumber && /*#__PURE__*/React.createElement(Select, selection))), !rendered && /*#__PURE__*/React.createElement(Loader, {
    ratio: viewport ? viewport.height / viewport.width : 1.414
  }));
});
Page.defaultProps = {};
export default Page;
//# sourceMappingURL=Page.js.map